import type { Prisma } from "@prisma/client";
import { buildPricingDetailsRelationSelect, PricingFieldGroup } from "../pricing/pricing.vars";

export const warrantyTypes = ["DO", "CNR", "TRC", "RCMO", "MRT", "GFA"] as const;
export const offersWarrantyTypes = ["DO", "TRC", "MRT", "GFA"] as const satisfies typeof warrantyTypes[number][];

export const isOfferWarrantyType = (warrantyType: typeof warrantyTypes[number]  | null): warrantyType is (typeof offersWarrantyTypes)[number] => {
	return warrantyType !== null && offersWarrantyTypes.includes(warrantyType as (typeof offersWarrantyTypes)[number]);
};

export const bindersSummarySelector = {
	id: true,
	reference: true,
	name: true,
	insurer: {
		select: {
			society: {
				select: {
					companyName: true,
				},
			},
		},
	},
	binderAmendments: true,
} satisfies Prisma.BinderSelect;

export const warrantyAccessibilityRuleSelector = {
	id: true,
	availabilityConstructionCostRule: true,
	availabilityConstructionCost: true,
	availabilityConstructionCostComparisonType: true,
	mandatoryConstructionCostRule: true,
	mandatoryConstructionCost: true,
	mandatoryConstructionCostComparisonType: true,
	availabilityActionTypeRule: true,
	availabilityActionType: true,
	mandatoryActionTypeRule: true,
	mandatoryActionType: true,
}

export const warrantyDetailsSelector = {
	id: true,
	name: true,
	shortName: true,
	article: true,
	isMandatory: true,
	isValueSelectable: true,
	limitIsUpdatable: true,
	franchiseIsUpdatable: true,
	warrantyLimit: {
		select: {
			id: true,
			limitType: true,
			value: true,
			limitValue: true,
		},
	},
	warrantyFranchise: {
		select: {
			id: true,
			value: true,
			comment: true,
		},
	},
	warrantyLimitOptions: { select: { limitOption: true } },
	warrantyFranchiseOptions: { select: { franchiseOption: true } },
	warrantyAccessibilityRule: {
		select: warrantyAccessibilityRuleSelector
	}
} satisfies Prisma.WarrantySelect;

export const baseBinderWarrantySelector = {
	id: true,
	name: true,
	shortName: true,
	warrantyType: true,
	isMandatory: true,
	binderAmendmentId: true,
	constructionSiteOffers: {
		select: {
			id: true,
			constructionSiteId: true
		}
	},
	warranties: {
		select: warrantyDetailsSelector,
		orderBy: {
			id: 'asc',
		},
	},
	warrantyAccessibilityRule: {
		select: warrantyAccessibilityRuleSelector,
	},
	binderAmendment: {
		select: {
			binder: {
				select: {
					id: true,
					binderType: true,
					insurerType: true,
					insurerId: true,
					insurer: {
						select: {
							society: {
								select: {
									siren: true,
									companyName: true,
								},
							},
						},
					},
				},
			},
		},
	},
} satisfies Prisma.BinderWarrantySelect;

export const binderWarrantyWithDependentWarrantiesSelector = {
	...baseBinderWarrantySelector,
	dependsOn: true,
	dependentWarranties: {
		select: baseBinderWarrantySelector
	},
} satisfies Prisma.BinderWarrantySelect;


export const paramConditionValueSelector = {
	id: true,
	conditionTypes: true,
	operator: true,
	floorComparisonType: true,
	floor: true,
	bearingStructure: true,
	underpinning: true,
	rga: true,
	sismic: true,
	constructionCostComparisonType: true,
	constructionCost: true,
	extensionComparisonType: true,
	extension: true,
	daysComparisonType: true,
	days: true,
	departments: {
		select: {
			department: {
				select: {
					id: true,
					code: true,
					name: true,
				}
			}
		}
	},
	SubscriptionParam: {
		select: { paramKey: true, constructionSiteTypeExclusive: true }
	}
} satisfies Prisma.ParamConditionValueSelect;

export const subscriptionParamSelector = {
	paramName: true,
	paramKey: true,
	constructionSiteTypeExclusive: true,
	paramConditionValues: {
		select: paramConditionValueSelector
	}
} satisfies Prisma.SubscriptionParamSelect;

export const subscriptionParamGroupSelector = {
	subscriptionParamType: true,
	params: {
		select: subscriptionParamSelector
	}
} satisfies Prisma.SubscriptionParamGroupSelect;

export const subscriptionParamsSelector =  {
	constructionSiteType: true,
	warrantyName: true,
	warrantyType: true,
	subscriptionParamGroups: {
		select: subscriptionParamGroupSelector
	}
} satisfies Prisma.SubscriptionParamsSelect;


export const binderSubscriptionParamsSelector = {
	automaticLimit: true,
	maximumLimit: true,
	onlineLimit: true,
	subscriptionParams: {
		select: subscriptionParamsSelector
	}
} satisfies Prisma.BinderSubscriptionParamsSelect;

export const binderAmendmentSelector = {
  id: true,
	name: true,
	commissionRate: true,
	effectiveDate: true,
	dueDate: true,
	endDate: true,
	constructionCostLimit: true,
	constructionCostLimitComparisonType: true,
	generalExclusions: true,
	specificExclusions: true,
	binderWarranties: {
		select: binderWarrantyWithDependentWarrantiesSelector,
		orderBy: {
			id: 'asc',
		},
	},
	binderSubscriptionParams: {
		select:  binderSubscriptionParamsSelector,
	},
} satisfies Prisma.BinderAmendmentSelect;

export const binderDetailsSelector = {
	id: true,
	reference: true,
	name: true,
	binderType: true,
	insurer: {
		select: {
			generalConditions: true,
			capital: true,
			registrationNumber: true,
			legalNotices: true,
			society: {
				select: {
					companyName: true,
					denomination: true,
					headOfficeAddress: {
						select: {
							streetNumber: true,
							streetName: true,
							postalCode: true,
							city: true,
						},
					},
				},
			},
		},
	},
	binderAmendments: {
		select: binderAmendmentSelector
	},
} satisfies Prisma.BinderSelect;

export const paramConditionType = ["FLOORS", "BASEMENTS", "BEARING_STRUCTURE", "UNDERPINNING", "CONSTRUCTION_COST", "RGA", "EXTENSION", "DAYS", "SISMIC", "DEPARTMENTS", "MANDATORY"] as const
export const binderType = ["DOTRC", "MRT", "GFA"] as const
export const logicalOperatorType = ["AND", "OR", "NONE"] as const


export const getLimitTypeDisplay = (limitType: string) => {
	switch (limitType) {
  	case "PERCENTAGE":
		case "CONSTRUCTION_COST_PERCENTAGE":
			return "%";
		case "FIXED":
			return "€";
		default:
		  return "";
	}
};
export const paramKeyGroups: Record<string, string> = {
	g2pro: "floorStudy",
	g2avp: "floorStudy",
	floors: "floorsBasements",
	basements: "floorsBasements",
	technicalControl_L: "technicalControl",
	technicalControl_L_LE: "technicalControl",
	technicalControl_L_P1_LE: "technicalControl",
	technicalControl_PS: "technicalControl",
	betStructure: "betStructure",
	moe: "moe",
	date: "constructionDate",
};

export const getParamKeyGroup = (paramKey: string): string => {
	return paramKeyGroups[paramKey] || paramKey;
};

export const paramKeyToDescription = {
	"moe": "Maîtrise d'oeuvre complète",
	"floorStudy": "Étude de sol",
	"betStructure": "Bureau d'étude structure",
	"technicalControl": "Contrôle technique",
	"floorsBasements": "Nombre de niveaux",
	"constructionDate": "Date de démarrage des travaux",
	"territoriality": "Territorialité",
	"doConditionsNotMet": "Dommages Ouvrages",
	"noAccess": "Accès aux produits",
}

export const warrantyValidationSelect = {
	id: true,
	warrantyType: true,
	binderAmendment: {
		select: {
			binder: {
				select: {
					insurer: {
						select: {
							society: {
								select: {
									siren: true,
								},
							},
						},
					},
				},
			},
		},
	},
} satisfies Prisma.BinderWarrantySelect;


export const constructionSiteOfferDocumentSelect = {
	id: true,
	constructionSiteId: true,
	binderWarrantyId: true,
	brokerFees: true,
} as const;

export const buildOfferDataSource = (
	warrantyType: "DO" | "TRC" | "MRT" | "CNR" | "RCMO",
	insurerType: "CARAVELA" | "ZURICH" | "AXERIA",
	pricingGroups: PricingFieldGroup[],
	label: string,
) => ({
	type: "relation" as const,
	config: {
		model: "constructionSiteOffer",
		field: "constructionSiteOffers",
		filter: {
			binderWarranty: {
				warrantyType,
				binderAmendment: {
					binder: {
						insurerType,
					},
				},
			},
		},
		first: true,
		select: {
			...constructionSiteOfferDocumentSelect,
			pricingDetails: buildPricingDetailsRelationSelect(pricingGroups),
		},
		label,
	},
});


export const warrantyDBKeys = [
	"DO_CARAVELA_GARANTIE_OBLIGATOIRE", 
	"DO_CARAVELA_DOMMAGES_SUBIS_EQUIPEMENTS", 
	"DO_CARAVELA_DOMMAGES_EXISTANTS", 
	"DO_CARAVELA_DOMMAGES_IMMATERIELS_CONSECUTIFS_APRES_RECEPTION",

	"CNR_CARAVELA_RESPONSABILITE_CIVILE_DECENNALE", 
	"CNR_CARAVELA_FACULTATIVES", 

	"TRC_CARAVELA_DOMMAGES_MATERIELS", 
	"TRC_CARAVELA_CATASTROPHES_NATURELLES", 
	"TRC_CARAVELA_MAINTENANCE_VISITE_APRES_TRAVAUX", 
	"TRC_CARAVELA_DOMMAGES_EXISTANTS", 

	"RCMO_CARAVELA_DOMMAGES_CORPORELS", 
	"RCMO_CARAVELA_DOMMAGES_MATERIELS", 
	"RCMO_CARAVELA_DOMMAGES_IMMATERIELS", 
	"RCMO_CARAVELA_DOMMAGES_ENVIRONNEMENT", 

	"DO_AXERIA_GARANTIE_OBLIGATOIRE",
	"DO_AXERIA_DOMMAGES_SUBIS_EQUIPEMENTS",
	"DO_AXERIA_DOMMAGES_EXISTANTS",
	"DO_AXERIA_DOMMAGES_IMMATERIELS_CONSECUTIFS_APRES_RECEPTION",

	"CNR_AXERIA_RESPONSABILITE_CIVILE_DECENNALE",
	"CNR_AXERIA_FACULTATIVES",

	"TRC_AXERIA_DOMMAGES_MATERIELS",
	"TRC_AXERIA_CATASTROPHES_NATURELLES",
	"TRC_AXERIA_MAINTENANCE_VISITE_APRES_TRAVAUX",
	"TRC_AXERIA_DOMMAGES_EXISTANTS",

	"RCMO_AXERIA_DOMMAGES_CORPORELS",
	"RCMO_AXERIA_DOMMAGES_MATERIELS",
	"RCMO_AXERIA_DOMMAGES_IMMATERIELS",
	"RCMO_AXERIA_DOMMAGES_ENVIRONNEMENT",

	"MRT_DO_ZURICH_DOMMAGES_OUVRAGE", 
	"MRT_DO_ZURICH_BON_FONCTIONNEMENT", 
	"MRT_DO_ZURICH_DOMMAGES_IMMATERIELS_CONSECUTIFS", 
	"MRT_DO_ZURICH_DOMMAGES_EXISTANTS", 

	"MRT_CNR_ZURICH_RC_DECENNALE", 
	"MRT_CNR_ZURICH_COMPLEMENTAIRE", 
	"MRT_CNR_ZURICH_BON_FONCTIONNEMENT", 
	"MRT_CNR_ZURICH_DOMMAGES_IMMATERIELS_CONSECUTIFS", 

	"MRT_TRC_ZURICH_DOMMAGES_OUVRAGE", 
	"MRT_TRC_ZURICH_DOMMAGES_EXISTANTS", 
	"MRT_TRC_ZURICH_MAINTENANCE_VISITE", 
	
	"MRT_RCMO_ZURICH_TOUS_DOMMAGES", 
	"MRT_RCMO_ZURICH_DEFENSE_RECROUS", 
	"MRT_RCMO_ZURICH_DOMMAGES_MATERIELS_ET_IMMATERIELS_CONSECUTIFS", 
	"MRT_RCMO_ZURICH_DOMMAGES_IMMATERIELS_NON_CONSECUTIFS",
] as const;

export type WarrantyDBKey = (typeof warrantyDBKeys)[number];

export type WarrantyDefinition = {
	key: string;
	dbKey: WarrantyDBKey;
	name: string;
	label?: string;
};

export const buildWarrantyDataSourceConfig = (
	warrantyType: "DO" | "TRC" | "MRT" | "CNR" | "RCMO",
	insurerType: "CARAVELA" | "ZURICH" | "AXERIA",
	warrantyDBKey: WarrantyDBKey,
	warrantyName: string,
	label?: string,
) => ({
	type: "relation" as const,
	config: {
		model: "constructionSiteOffer",
		field: "constructionSiteOffers",
		filter: {
			binderWarranty: {
				warrantyType,
				binderAmendment: {
					binder: {
						insurerType,
					},
				},
			},
		},
		first: true,
		select: {
			constructionSiteWarrantyOffers: {
				where: {
					warranty: {
								key: {
									equals: warrantyDBKey,
								},
							},
				},
				take: 1,
				select: {
					warranty: {
								select: {
									name: true,
								},
							},
							limitOption: {
								select: {
									limitType: true,
									value: true,
								},
							},
				}
			}
		},
		label: label ?? warrantyName,
		},
});

export const CARAVELA_DO_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "GarantieObligatoire",
		dbKey: "DO_CARAVELA_GARANTIE_OBLIGATOIRE",
		name: "Garantie Obligatoire (au sens de l'article L242-1 du Code des Assurances)",
	},
	{
		key: "GarantieEquipements",
		dbKey: "DO_CARAVELA_DOMMAGES_SUBIS_EQUIPEMENTS",
		name: "Garantie de Dommages subis par les éléments d'Équipements",
	},
	{
		key: "GarantieExistants",
		dbKey: "DO_CARAVELA_DOMMAGES_EXISTANTS",
		name: "Garantie des Dommages aux existants",
	},
	{
		key: "GarantieImmateriels",
		dbKey: "DO_CARAVELA_DOMMAGES_IMMATERIELS_CONSECUTIFS_APRES_RECEPTION",
		name: "Garantie de Dommages Immatériels Consécutifs Après Réception",
	},
];

export const CARAVELA_CNR_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "ResponsabiliteCivileDecennale",
		dbKey: "CNR_CARAVELA_RESPONSABILITE_CIVILE_DECENNALE",
		name: "Responsabilité Civile Décennale",
	},
	{
		key: "GarantiesFacultatives",
		dbKey: "CNR_CARAVELA_FACULTATIVES",
		name: "Garanties Facultatives",
	},
];

export const CARAVELA_TRC_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "DommagesMateriels",
		dbKey: "TRC_CARAVELA_DOMMAGES_MATERIELS",
		name: "Garantie de dommages matériels à l'ouvrage pendant la durée des travaux",
	},
	{
		key: "CatastrophesNaturelles",
		dbKey: "TRC_CARAVELA_CATASTROPHES_NATURELLES",
		name: "Garantie Catastrophes Naturelles",
	},
	{
		key: "MaintenanceVisite",
		dbKey: "TRC_CARAVELA_MAINTENANCE_VISITE_APRES_TRAVAUX",
		name: "Garantie de maintenance visite après travaux",
	},
	{
		key: "GarantieExistants",
		dbKey: "TRC_CARAVELA_DOMMAGES_EXISTANTS",
		name: "Garantie des dommages aux existants",
	},
];

export const CARAVELA_RCMO_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "DommagesCorporels",
		dbKey: "RCMO_CARAVELA_DOMMAGES_CORPORELS",
		name: "Dommages corporels",
	},
	{
		key: "DommagesMateriels",
		dbKey: "RCMO_CARAVELA_DOMMAGES_MATERIELS",
		name: "Dommages matériels",
	},
	{
		key: "DommagesImmateriels",
		dbKey: "RCMO_CARAVELA_DOMMAGES_IMMATERIELS",
		name: "Dommages immatériels",
	},
	{
		key: "AtteinteEnvironnement",
		dbKey: "RCMO_CARAVELA_DOMMAGES_ENVIRONNEMENT",
		name: "Tous dommages confondus d'atteinte à l'environnement",
	},
];

export const MRT_DO_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "GarantieObligatoire",
		dbKey: "MRT_DO_ZURICH_DOMMAGES_OUVRAGE",
		name: "Garantie Obligatoire Dommages-Ouvrage",
	},
	{
		key: "GarantieBonFonctionnement",
		dbKey: "MRT_DO_ZURICH_BON_FONCTIONNEMENT",
		name: "Garantie complémentaire Bon fonctionnement",
	},
	{
		key: "GarantieImmateriels",
		dbKey: "MRT_DO_ZURICH_DOMMAGES_IMMATERIELS_CONSECUTIFS",
		name: "Garantie complémentaire Dommages Immatériels Consécutifs",
	},
	{
		key: "GarantieExistants",
		dbKey: "MRT_DO_ZURICH_DOMMAGES_EXISTANTS",
		name: "Garantie complémentaire - Dommages aux Existants non techniquement indivisibles",
	},
];

export const MRT_CNR_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "GarantieObligatoireDecennale",
		dbKey: "MRT_CNR_ZURICH_RC_DECENNALE",
		name: "Garantie obligatoire RC Décennale",
	},
	{
		key: "GarantieComplementaire",
		dbKey: "MRT_CNR_ZURICH_COMPLEMENTAIRE",
		name: "Garantie complémentaire",
	},
	{
		key: "GarantieBonFonctionnement",
		dbKey: "MRT_CNR_ZURICH_BON_FONCTIONNEMENT",
		name: "Bon fonctionnement",
	},
	{
		key: "GarantieImmateriels",
		dbKey: "MRT_CNR_ZURICH_DOMMAGES_IMMATERIELS_CONSECUTIFS",
		name: "Garantie complémentaire Dommages Immatériels consécutifs",
	},
];

export const MRT_TRC_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "DommagesOuvrage",
		dbKey: "MRT_TRC_ZURICH_DOMMAGES_OUVRAGE",
		name: "Dommages à l'ouvrage",
	},
	{
		key: "GarantieExistants",
		dbKey: "MRT_TRC_ZURICH_DOMMAGES_EXISTANTS",
		name: "Dommages aux Existants",
	},
	{
		key: "MaintenanceVisite",
		dbKey: "MRT_TRC_ZURICH_MAINTENANCE_VISITE",
		name: "Maintenance Visite",
	},
];

export const MRT_RCMO_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "TousDommagesConfondus",
		dbKey: "MRT_RCMO_ZURICH_TOUS_DOMMAGES",
		name: "Tous Dommages Confondus dont :",
	},
	{
		key: "DefenseRecours",
		dbKey: "MRT_RCMO_ZURICH_DEFENSE_RECROUS",
		name: "- Défense Recours",
	},
	{
		key: "MaterielsEtImmaterielsConsecutifs",
		dbKey: "MRT_RCMO_ZURICH_DOMMAGES_MATERIELS_ET_IMMATERIELS_CONSECUTIFS",
		name: "- Dommages Matériels et Immatériels Consécutifs",
	},
	{
		key: "ImmaterielsNonConsecutifs",
		dbKey: "MRT_RCMO_ZURICH_DOMMAGES_IMMATERIELS_NON_CONSECUTIFS",
		name: "- Dommages Immatériels Consécutifs",
	},
];


export const AXERIA_DO_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "GarantieObligatoire",
		dbKey: "DO_AXERIA_GARANTIE_OBLIGATOIRE",
		name: "Garantie Obligatoire (au sens de l'article L242-1 du Code des Assurances)",
	},
	{
		key: "GarantieEquipements",
		dbKey: "DO_AXERIA_DOMMAGES_SUBIS_EQUIPEMENTS",
		name: "Garantie de Dommages subis par les éléments d'Équipements",
	},
	{
		key: "GarantieExistants",
		dbKey: "DO_AXERIA_DOMMAGES_EXISTANTS",
		name: "Garantie des Dommages aux existants",
	},
	{
		key: "GarantieImmateriels",
		dbKey: "DO_AXERIA_DOMMAGES_IMMATERIELS_CONSECUTIFS_APRES_RECEPTION",
		name: "Garantie de Dommages Immatériels Consécutifs Après Réception",
	},
];

export const AXERIA_CNR_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "ResponsabiliteCivileDecennale",
		dbKey: "CNR_AXERIA_RESPONSABILITE_CIVILE_DECENNALE",
		name: "Responsabilité Civile Décennale",
	},
	{
		key: "GarantiesFacultatives",
		dbKey: "CNR_AXERIA_FACULTATIVES",
		name: "Garanties Facultatives",
	},
];

export const AXERIA_TRC_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "DommagesMateriels",
		dbKey: "TRC_AXERIA_DOMMAGES_MATERIELS",
		name: "Garantie de dommages matériels à l'ouvrage pendant la durée des travaux",
	},
	{
		key: "CatastrophesNaturelles",
		dbKey: "TRC_AXERIA_CATASTROPHES_NATURELLES",
		name: "Garantie Catastrophes Naturelles",
	},
	{
		key: "MaintenanceVisite",
		dbKey: "TRC_AXERIA_MAINTENANCE_VISITE_APRES_TRAVAUX",
		name: "Garantie de maintenance visite après travaux",
	},
	{
		key: "GarantieExistants",
		dbKey: "TRC_AXERIA_DOMMAGES_EXISTANTS",
		name: "Garantie des dommages aux existants",
	},
];

export const AXERIA_RCMO_WARRANTIES: WarrantyDefinition[] = [
	{
		key: "DommagesCorporels",
		dbKey: "RCMO_AXERIA_DOMMAGES_CORPORELS",
		name: "Dommages corporels",
	},
	{
		key: "DommagesMateriels",
		dbKey: "RCMO_AXERIA_DOMMAGES_MATERIELS",
		name: "Dommages matériels",
	},
	{
		key: "DommagesImmateriels",
		dbKey: "RCMO_AXERIA_DOMMAGES_IMMATERIELS",
		name: "Dommages immatériels",
	},
	{
		key: "AtteinteEnvironnement",
		dbKey: "RCMO_AXERIA_DOMMAGES_ENVIRONNEMENT",
		name: "Tous dommages confondus d'atteinte à l'environnement",
	},
];